/**
 * SoleadLab 2.0 — State Snapshot Store (C2.5)
 *
 * Zustand store for site-scoped and asset-scoped state snapshot management.
 * Consumes the C2.3 (recompute) and C2.4 (timeline + latest) backend endpoints
 * via stateApi. All state computation is owned by the backend; this store is
 * a read/trigger layer only — it never re-derives status, confidence, or delta
 * values from raw data. Every value displayed in the UI is lifted verbatim
 * from the API response.
 */

import { create } from 'zustand';
import type {
  SiteStateSnapshot,
  AssetStateSnapshot,
  RecomputeResult,
} from '@/types/state';
import * as stateApi from '@/services/stateApi';

// ─── Per-asset cache entries ──────────────────────────────────────────────────

export interface AssetSnapshotCache {
  latest: AssetStateSnapshot | null;
  loading: boolean;
  error: string | null;
}

export interface AssetHistoryCache {
  items: AssetStateSnapshot[];
  total: number;
  page: number;
  loading: boolean;
}

// ─── Store interface ──────────────────────────────────────────────────────────

interface StateStore {
  currentSiteId: string | null;

  // Latest site snapshot
  siteLatest: SiteStateSnapshot | null;
  siteLatestLoading: boolean;
  siteLatestError: string | null;

  // Paginated site history
  siteHistory: SiteStateSnapshot[];
  siteHistoryTotal: number;
  siteHistoryPage: number;
  siteHistoryLoading: boolean;

  // Per-asset snapshot cache (keyed by nodeId)
  assetCache: Record<string, AssetSnapshotCache>;
  assetHistoryCache: Record<string, AssetHistoryCache>;

  // Recompute state
  recomputeStatus: 'idle' | 'running' | 'done' | 'error';
  lastRecompute: RecomputeResult | null;

  // Actions
  setSiteId: (siteId: string | null) => void;

  // Site-level
  loadSiteLatest: () => Promise<void>;
  loadSiteHistory: (page?: number) => Promise<void>;

  // Asset-level (per nodeId)
  loadAssetLatest: (nodeId: string) => Promise<void>;
  loadAssetHistory: (nodeId: string, page?: number) => Promise<void>;

  // Recompute
  triggerRecompute: (scope?: 'all' | 'assets' | 'site') => Promise<void>;
}

// ─── Store implementation ─────────────────────────────────────────────────────

export const useStateStore = create<StateStore>((set, get) => ({
  currentSiteId: null,

  siteLatest: null,
  siteLatestLoading: false,
  siteLatestError: null,

  siteHistory: [],
  siteHistoryTotal: 0,
  siteHistoryPage: 1,
  siteHistoryLoading: false,

  assetCache: {},
  assetHistoryCache: {},

  recomputeStatus: 'idle',
  lastRecompute: null,

  setSiteId: (siteId: string | null) => {
    const prev = get().currentSiteId;
    set({ currentSiteId: siteId });
    if (siteId !== prev) {
      // Clear all stale data when switching sites
      set({
        siteLatest: null,
        siteLatestError: null,
        siteHistory: [],
        siteHistoryTotal: 0,
        siteHistoryPage: 1,
        assetCache: {},
        assetHistoryCache: {},
        recomputeStatus: 'idle',
        lastRecompute: null,
      });
    }
    if (siteId) {
      get().loadSiteLatest();
    }
  },

  // ── Site latest ─────────────────────────────────────────────────────────────

  loadSiteLatest: async () => {
    const { currentSiteId } = get();
    if (!currentSiteId) return;

    set({ siteLatestLoading: true, siteLatestError: null });
    try {
      const snapshot = await stateApi.getSiteStateLatest(currentSiteId);
      set({ siteLatest: snapshot, siteLatestLoading: false });
    } catch (err: unknown) {
      const status = (err as { status?: number }).status;
      if (status === 404) {
        // No snapshots yet — not an error, just empty state
        set({ siteLatest: null, siteLatestLoading: false });
      } else {
        console.error('Failed to load site state:', err);
        set({ siteLatestError: 'Failed to load site state', siteLatestLoading: false });
      }
    }
  },

  // ── Site history ────────────────────────────────────────────────────────────

  loadSiteHistory: async (page = 1) => {
    const { currentSiteId } = get();
    if (!currentSiteId) return;

    set({ siteHistoryLoading: true });
    try {
      const result = await stateApi.listSiteStateSnapshots(currentSiteId, page);
      set({
        siteHistory: result.items,
        siteHistoryTotal: result.total,
        siteHistoryPage: page,
        siteHistoryLoading: false,
      });
    } catch (err) {
      console.error('Failed to load site state history:', err);
      set({ siteHistoryLoading: false });
    }
  },

  // ── Asset latest (cached per nodeId) ────────────────────────────────────────

  loadAssetLatest: async (nodeId: string) => {
    const { currentSiteId, assetCache } = get();
    if (!currentSiteId) return;

    // Set loading state for this nodeId without touching other nodes
    set({
      assetCache: {
        ...assetCache,
        [nodeId]: { latest: assetCache[nodeId]?.latest ?? null, loading: true, error: null },
      },
    });

    try {
      const snapshot = await stateApi.getAssetStateLatest(currentSiteId, nodeId);
      set((s) => ({
        assetCache: {
          ...s.assetCache,
          [nodeId]: { latest: snapshot, loading: false, error: null },
        },
      }));
    } catch (err: unknown) {
      const status = (err as { status?: number }).status;
      if (status === 404) {
        // No snapshot yet — set null without error flag
        set((s) => ({
          assetCache: {
            ...s.assetCache,
            [nodeId]: { latest: null, loading: false, error: null },
          },
        }));
      } else {
        console.error(`Failed to load asset state for ${nodeId}:`, err);
        set((s) => ({
          assetCache: {
            ...s.assetCache,
            [nodeId]: { latest: null, loading: false, error: 'Failed to load asset state' },
          },
        }));
      }
    }
  },

  // ── Asset history (cached per nodeId) ───────────────────────────────────────

  loadAssetHistory: async (nodeId: string, page = 1) => {
    const { currentSiteId, assetHistoryCache } = get();
    if (!currentSiteId) return;

    set({
      assetHistoryCache: {
        ...assetHistoryCache,
        [nodeId]: {
          items: assetHistoryCache[nodeId]?.items ?? [],
          total: assetHistoryCache[nodeId]?.total ?? 0,
          page,
          loading: true,
        },
      },
    });

    try {
      const result = await stateApi.listAssetStateSnapshots(currentSiteId, nodeId, page);
      set((s) => ({
        assetHistoryCache: {
          ...s.assetHistoryCache,
          [nodeId]: { items: result.items, total: result.total, page, loading: false },
        },
      }));
    } catch (err) {
      console.error(`Failed to load asset history for ${nodeId}:`, err);
      set((s) => ({
        assetHistoryCache: {
          ...s.assetHistoryCache,
          [nodeId]: {
            ...s.assetHistoryCache[nodeId],
            loading: false,
          },
        },
      }));
    }
  },

  // ── Recompute ────────────────────────────────────────────────────────────────

  triggerRecompute: async (scope = 'all') => {
    const { currentSiteId } = get();
    if (!currentSiteId) return;

    set({ recomputeStatus: 'running' });
    try {
      const result = await stateApi.triggerRecompute(currentSiteId, scope);
      set({ recomputeStatus: 'done', lastRecompute: result });
      // Refresh latest snapshot after recompute
      get().loadSiteLatest();
    } catch (err) {
      console.error('Recompute failed:', err);
      set({ recomputeStatus: 'error' });
    }
  },
}));
